import React, { useState, useEffect } from "react";
import { Table, Typography, Row, Col, Button, Input } from "antd";
import { LinkOutlined, TeamOutlined } from "@ant-design/icons";
import moment from "moment";
import { platformApi } from "../../../../api";
import MergeLedgerModal from "../MergeLedgerModal";
import MergeAuditDetailModal from "../MergeAuditDetailModal";

const { Text, Title } = Typography;

function DuplicateSuggestionsTable({ onMergeClick, refreshTrigger }) {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchDuplicates();
  }, [refreshTrigger]);

  const fetchDuplicates = () => {
    setLoading(true);
    platformApi.get("/api/customer/duplicates")
      .then(res => {
        setLoading(false);
        if (res.data.code === 200 && res.data.response?.data) {
          setDuplicates(res.data.response.data);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const columns = [
    {
      title: "S.No",
      key: "index",
      width: "12%",
      render: (_, __, i) => i + 1,
    },
    {
      title: "Mobile No",
      dataIndex: "phone",
      key: "phone",
      width: "25%",
    },
    {
      title: "Duplicate Customers",
      key: "customers",
      width: "43%",
      render: (_, record) => {
        const names = (record.customers || []).map(c => c.name || "-");
        return <span>{names.join(", ") || "-"}</span>;
      }
    },
    {
      title: "Action",
      key: "action",
      width: "20%",
      render: (_, record) => (
        <Button
          size="small"
          style={{ backgroundColor: "#006d75", color: "white", borderColor: "#006d75" }}
          onClick={() => onMergeClick(record.customers || [])}
        >
          Merge
        </Button>
      ),
    },
  ];

  const filteredDuplicates = duplicates.filter(d => d.phone && d.phone.includes(searchText));

  return (
    <div style={{ 
      border: "1px solid #006d75", 
      borderRadius: "8px", 
      padding: "16px", 
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      backgroundColor: "#fff"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <Title level={4} style={{ margin: 0, color: "#006d75" }}>
          <TeamOutlined style={{ marginRight: 8 }} />
          Auto Suggestions
        </Title>
        <Input.Search
          placeholder="Search Mobile No"
          allowClear
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 200 }}
        />
      </div>
      <Table
        rowKey="phone"
        columns={columns}
        dataSource={filteredDuplicates}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        onRow={() => ({ style: { height: "70px" } })}
      />
    </div>
  );
}

export default function MergeLedgerTable(props) {
  const [dataSource, setDataSource] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [spinning, setSpinning] = useState(false);
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const [auditDetailVisible, setAuditDetailVisible] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [prefillDuplicates, setPrefillDuplicates] = useState(null);
  const [refreshSuggestions, setRefreshSuggestions] = useState(0);

  const isModalOpen = props.modalOpen !== undefined ? props.modalOpen : internalModalOpen;
  const setIsModalOpen = props.setModalOpen || setInternalModalOpen;

  const handleOpenMergeModal = (duplicates) => {
    setPrefillDuplicates(duplicates);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!isModalOpen) {
      setPrefillDuplicates(null);
    }
  }, [isModalOpen]);

  const isActive = props.isActive;

  useEffect(() => {
    if (isActive) {
      fetchAuditList();
    }
  }, [isActive, page]);

  const fetchAuditList = () => {
    setSpinning(true);
    platformApi
      .get(`/api/customer/merge-audit?page=${page}&size=${limit}&_t=${Date.now()}`)
      .then((res) => {
        setSpinning(false);
        if (res.data.code === 200) {
          const { records, count } = res.data.response.data;
          setDataSource(records || []);
          setCount(count || 0);
        }
      })
      .catch(() => setSpinning(false));
  };

  const columns = [
    {
      title: "S.No",
      key: "index",
      width: "8%",
      render: (_, __, index) => <span>{(page - 1) * limit + index + 1}</span>,
    },
    {
      title: "From Ledger",
      key: "fromLedger",
      width: "36%",
      render: (_, record) => {
        const name = record.fromLedger || "-";
        const phone = record.mobileNo || "";
        return (
          <span className="linkylink">
            {name}{phone ? ` - ${phone}` : ""}
          </span>
        );
      },
    },
    {
      title: "To Ledger",
      key: "toLedger",
      width: "36%",
      render: (_, record) => {
        const name = record.toLedger || "-";
        const phone = record.mobileNo || "";
        return (
          <span className="linkylink">
            {name}{phone ? ` - ${phone}` : ""}
          </span>
        );
      },
    },
    {
      title: "Merged On",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "20%",
      render: (val) => <span>{val ? moment(val).format("DD/MM/YYYY HH:mm") : "-"}</span>,
    },
  ];

  return (
    <div>
      <Row gutter={16}>
        {/* Left Column: Merge History */}
        <Col span={14}>
          <div style={{ padding: "16px 10px 0 10px", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", minHeight: "32px" }}>
              <Title level={4} style={{ margin: 0, color: "#006d75" }}>
                <LinkOutlined style={{ marginRight: 8 }} />
                Merged Records
              </Title>
            </div>
            <Table
              rowKey={(record) => record.id}
              dataSource={dataSource}
              columns={columns}
              tableLayout="fixed"
              style={{ cursor: "pointer" }}
              loading={spinning}
              pagination={{
                current: page,
                pageSize: limit,
                total: count,
                onChange: (p) => setPage(p),
                showQuickJumper: true,
              }}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedAudit(record);
                  setAuditDetailVisible(true);
                },
                style: { cursor: "pointer", height: "70px" },
              })}
            />
          </div>
        </Col>

        {/* Right Column: Duplicate Suggestions */}
        <Col span={10}>
          <DuplicateSuggestionsTable 
            onMergeClick={handleOpenMergeModal} 
            refreshTrigger={refreshSuggestions}
          />
        </Col>
      </Row>

      {/* Merge Ledger Form Modal */}
      {isModalOpen && (
        <MergeLedgerModal
          open={isModalOpen}
          prefillDuplicates={prefillDuplicates}
          close={() => setIsModalOpen(false)}
          onMergeSuccess={() => {
            setPage(1);
            fetchAuditList();
            setRefreshSuggestions(prev => prev + 1);
          }}
        />
      )}

      {/* Audit Detail Modal */}
      <MergeAuditDetailModal
        visible={auditDetailVisible}
        onClose={() => {
          setAuditDetailVisible(false);
          setSelectedAudit(null);
        }}
        record={selectedAudit}
      />
    </div>
  );
}
